import type { GetStaticPaths, GetStaticProps } from 'next';
import React, { useEffect } from 'react';

import Layout from '../../components/Layout';
import Series from '../../components/PageTypes/Series';
import { FIFTEEN_MINUTES_IN_SECONDS, SERIES_PAGE_TYPE } from '../../constants';
import { DEFAULT_CURRENCY_CODE } from '../../constants/locations';
import { ShopifyProductRecommendationsGateway } from '../../gateways/ShopifyProductRecommendationGateway';
import { imageOgStaticLoader } from '../../helpers/og-image';
import { TrackPageView } from '../../utils/analytics';
import cache from '../../utils/cache';
import { addSlugToProducts } from '../../utils/product';
import { fetchAllProductsCount } from '../../utils/shopify/fetchAllProducts';
import { fetchProductsByIds } from '../../utils/shopify/fetchProductsByIds';
import { getStoryblokPage, useStoryblok } from '../../utils/storyblok';
import { pageTranslations } from '../../utils/translations';

const SERIES_ROOT_PATH = 'series';

export default function Page({
  locale,
  locales,
  ogImageUrl,
  pageData,
  preview,
  story,
}: PageProps) {
  useEffect(() => {
    TrackPageView({ page: SERIES_PAGE_TYPE });
  }, []);

  const enableBridge = preview;
  story = useStoryblok(story, enableBridge, locale);

  story.content = {
    ...story.content,
    series: (pageData as SeriesType).series,
    additionalProducts: (pageData as SeriesType).additionalProducts,
    worksCount: (pageData as SeriesType).worksCount,
  };

  return (
    <Layout
      description={story.content.seo?.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={story.content.seo?.title}
    >
      <Series
        blok={story.content}
        recommendations={(pageData as SeriesType).recommendations}
      />
    </Layout>
  );
}

const getProductIds = (products: Array<any> | undefined) => {
  if (!products) {
    return [];
  }

  return products.reduce(
    (filteredProducts, product) => (
      product.content.id[0].id.length === 0
        ? null
        : filteredProducts.push(product.content.id[0].id),
      filteredProducts
    ),
    [],
  );
};

export const getStaticProps: GetStaticProps = async ({
  locale,
  locales,
  params,
  preview = false,
}) => {
  const slug = params?.slug ? (params.slug as string[]).join('/') : 'home';
  const storyblokPath = `cdn/stories/${SERIES_ROOT_PATH}/${slug}`;
  const page = await getStoryblokPage(storyblokPath, {
    locale,
    preview,
    resolve_relations: ['relatedArtist.artist', 'seriesProducts.products'],
  });

  if (page === null) {
    return {
      notFound: true,
    };
  }

  const seriesProducts = page.data?.story?.content?.series?.[0]?.products;
  if (!seriesProducts || seriesProducts.length === 0) {
    console.warn(
      `Series page with slug "${slug}" is missing series data from Storyblok.`,
    );
    return {
      notFound: true,
    };
  }

  try {
    const [worksCount, recommendations, seriesFromShopify] = await Promise.all([
      fetchAllProductsCount('tag:Listing page'),
      new ShopifyProductRecommendationsGateway(cache).getRecommendations(
        page.data.story.content.artist.length > 0
          ? page.data.story.content.artist[0].artist.name
          : '',
      ),
      fetchProductsByIds(
        getProductIds(seriesProducts),
        DEFAULT_CURRENCY_CODE,
        'Admin',
      ),
    ]);

    const pageData = {
      worksCount,
      recommendations,
      series:
        page.data.story.content.series &&
        page.data.story.content.series.length > 0
          ? addSlugToProducts(seriesFromShopify, seriesProducts)
          : [],
      additionalProducts:
        page.data.story.content.additionalProducts &&
        page.data.story.content.additionalProducts.length > 0
          ? addSlugToProducts(
              await fetchProductsByIds(
                getProductIds(
                  page.data.story.content.additionalProducts[0].products,
                ),
                DEFAULT_CURRENCY_CODE,
                'Admin',
              ),
              page.data.story.content.additionalProducts[0].products,
            )
          : [],
    };

    const story = page.data ? page.data.story : false;

    const ogImageUrl = await imageOgStaticLoader({
      overlineKey:
        story.content?.medium && story.content.medium?.length > 0
          ? `${story.content.medium[0]} editions`
          : 'Editions',
      titleKey: story.content.seo?.title,
      imageSrc: story.content.thumbnail?.filename
        ? story.content.thumbnail.filename
        : '',
      imageWidth: 510,
      imageHeight: 510,
      locale,
    });

    return {
      props: {
        story: page.data ? page.data.story : false,
        pageData,
        preview,
        locale,
        locales,
        ogImageUrl,
        ...(locale && (await pageTranslations(locale, ['series']))),
      },
      revalidate: FIFTEEN_MINUTES_IN_SECONDS,
    };
  } catch (error) {
    console.error(`Failed to build series page "${slug}":`, error);

    return {
      notFound: true,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};
